import React from 'react';
import { Mail, Send } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

export default function ContactPage() {
  const { user } = useAuth();
  const [form, setForm] = React.useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const sendMessageMutation = useMutation({
    mutationFn: async (payload: typeof form) => {
      const res = await api.post('/contact', payload);
      return res.data;
    },
    onSuccess: () => {
      window.dispatchEvent(
        new CustomEvent('toast', {
          detail: { type: 'success', message: 'Mensagem enviada com sucesso!' },
        })
      );
      setForm({ name: user?.name || '', email: user?.email || '', subject: '', message: '' });
      setErrors({});
    },
    onError: (err: any) => {
      window.dispatchEvent(
        new CustomEvent('toast', {
          detail: { type: 'error', message: err?.message || 'Falha ao enviar mensagem' },
        })
      );
    },
  });

  const handleChange = (key: string, value: string) => {
    setForm((s) => ({ ...s, [key]: value }));
    if (errors[key]) {
      setErrors((e) => {
        const newErrors = { ...e };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!form.message.trim()) {
      newErrors.message = 'Mensagem é obrigatória';
    } else if (form.message.trim().length < 10) {
      newErrors.message = 'A mensagem deve ter no mínimo 10 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await sendMessageMutation.mutateAsync(form);
  };

  const handleOpenGmail = () => {
    const mailtoLink = `mailto:marlon.klc@gmail.com?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(`Nome: ${form.name}\nEmail: ${form.email}\n\nMensagem:\n${form.message}`)}`;
    window.location.href = mailtoLink;
  };

  return (
    <Layout>
      <div className="flex flex-col h-full bg-gray-100">
        <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-6">
          <div className="text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Entre em Contato</h1>
            <p className="text-gray-600 text-sm mt-2">Tem alguma dúvida ou sugestão? Envie sua mensagem</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg shadow p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Nome <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Seu nome"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Email <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="seu@email.com"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Assunto</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => handleChange('subject', e.target.value)}
                    placeholder="Assunto da mensagem"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Mensagem <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder="Escreva sua mensagem aqui..."
                    rows={6}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none ${
                      errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={sendMessageMutation.isPending}
                    className="flex-1 bg-gray-900 text-white py-2 rounded-md hover:bg-gray-800 font-medium disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {sendMessageMutation.isPending ? 'Enviando...' : 'Enviar Mensagem'}
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenGmail}
                    className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-50 font-medium flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Abrir no Gmail
                  </button>
                </div>

                <p className="text-xs text-gray-500 text-center pt-2">
                  Sua mensagem será salva no sistema e você pode optar por enviá-la diretamente via Gmail
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
